import { logging, API_BASE_URL } from '@/config/env';
import type {
  User,
  UserListResponse,
  ApiUserListResponse,
  UserQueryParams,
} from './users.dto';

import { redirectToLogin } from '../../utils/sessionExpired';
// Custom logging function
const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class UsersService {
  private baseUrl: string;
  private logoutCallback: (() => void) | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // Check for 401 Unauthorized
    if (response.status === 401) {
      customLog('Session expired - triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
      redirectToLogin();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      // Try to parse error message from response
      try {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      } catch (e) {
        // If parsing fails, continue with original error handling
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', data);
    return data;
  }

  async getUsers(
    token: string,
    params: UserQueryParams = {}
  ): Promise<UserListResponse> {
    customLog('API Service: getUsers called with params:', params);

    const queryString = new URLSearchParams();
    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.status !== undefined)
      queryString.append('status', params.status ? 'true' : 'false');
    if (params.gender) queryString.append('gender', params.gender);
    if (params.role) queryString.append('role', params.role);
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);

    const endpoint = `/admin/users${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    customLog('API Service: Final endpoint:', endpoint);
    customLog(
      'API Service: Authorization header:',
      `Bearer ${token.substring(0, 20)}...`
    );

    const response = await this.request<ApiUserListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    customLog('=== API Service Response Analysis ===');
    customLog('Full response object:', response);
    customLog('response keys:', Object.keys(response));
    customLog('response.status:', (response as any).status);
    customLog('response.message:', (response as any).message);
    customLog('response.data:', response.data);
    customLog('response.data type:', typeof response.data);

    // Check if response.data exists
    if (!response.data) {
      customLog('API Service: Warning - response.data is undefined');
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: 10 },
      };
    }

    // Check if response.data has the nested structure or is flat
    const responseData = response.data as any;

    // If response.data is an array (flat structure), use response metadata
    if (Array.isArray(responseData)) {
      const result = {
        data: responseData,
        meta: {
          total: (response as any).total || 0,
          page: (response as any).page || 1,
          lastPage: (response as any).lastPage || 1,
          limit: params.limit || 10,
        },
      };

      customLog('=== Flat Structure Result ===');
      customLog('result.data length:', result.data.length);
      customLog('result.meta:', result.meta);

      return result;
    }

    // Handle nested structure
    const result = {
      data: responseData.data || responseData || [],
      meta: {
        total: responseData.total || (response as any).total || 0,
        page: responseData.page || (response as any).page || 1,
        lastPage: responseData.totalPages || (response as any).lastPage || 1,
        limit: params.limit || 10,
      },
    };

    customLog('=== Final Parsed Result ===');
    customLog('result.data length:', result.data.length);
    customLog('result.meta:', result.meta);

    return result;
  }

  async getUser(token: string, id: number): Promise<User> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: User;
    }>(`/admin/users/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async createUser(token: string, userData: Partial<User>): Promise<User> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: User;
    }>('/admin/users', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });
    return response.data;
  }

  async updateUser(
    token: string,
    id: number,
    userData: Partial<User>
  ): Promise<User> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: User;
    }>(`/admin/users/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });
    return response.data;
  }

  async deleteUser(token: string, id: number): Promise<{ message: string }> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data?: unknown;
    }>(`/admin/users/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { message: response.message };
  }

  async toggleUserStatus(token: string, id: number): Promise<User> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: User;
    }>(`/admin/users/${id}/toggle-status`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }
}

export const usersService = new UsersService();
